import React, { useEffect, useState } from "react";

import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import { InfoCircleOutlined, MoreOutlined } from "@ant-design/icons";

import ConfirmationModal from "../../Components/ConfirmationModal/ConfirmationModal";
import ManagePlanModal from "../../Components/ManagePlanModal";
import useDebounce from "../../hooks/useDebounce";
import {
  getAdminCreatorById,
  getRefernEarnOrders,
  refundTierOrder,
} from "../api";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};

const tierMap = {
  tier1: "Basic",
  tier2: "Pro",
  tier3: "Advanced",
  tier4: "Ultimate",
  whatsapp: "Whatsapp",
};

const platformSetupDisplayMap = {
  "freedom-plan": "Freedom",
  "enterprise-plan": "Enterprise",
  "tagmango-plan": "TagMango",
  freedom: "Freedom",
  enterprise: "Enterprise",
  tagmango: "TagMango",
};

const tierOptionsBySetup = {
  tagmango: [{ value: "tier1", label: "Basic" }],
  freedom: [
    { value: "tier1", label: "Basic" },
    { value: "tier2", label: "Pro" },
    { value: "tier3", label: "Advanced" },
  ],
  enterprise: [
    { value: "tier1", label: "Basic" },
    { value: "tier2", label: "Pro" },
    { value: "tier3", label: "Advanced" },
    { value: "tier4", label: "Ultimate" },
  ],
};

const CREATOR_NAME_LIMIT = 25;

const truncate = (str, limit) =>
  str && str.length > limit ? `${str.slice(0, limit)}…` : str;

const isFeatureSalePurchase = (record) =>
  record?.saleItem?.subcategory === "feature" ||
  (Array.isArray(record?.saleItem?.features) &&
    record.saleItem.features.length > 0);

// An AI credit top-up buys neither a plan nor a feature, and it is one-off: it
// carries no subscription even when subscriptionCharge comes back populated.
const isAiCreditPurchase = (record) =>
  record?.transactionType === "ai_credit_purchase";

const isPartialRefundEligible = (record) => {
  if (isFeatureSalePurchase(record)) return false;
  return (
    !!record?.paymentPlan &&
    record.paymentPlan !== "monthly" &&
    (!record?.noCostEmiDiscount || record?.noCostEmiDiscount <= 0)
  );
};

const getRemainingRefundable = (record) => {
  // For partial_refunded orders, backend returns the exact remaining
  // balance (net + GST) in refundedDetails. Prefer that when present so
  // the next partial refund is capped precisely.
  const details = record?.refundedDetails;
  if (details) {
    return (
      (details.remainingAmount?.inr || 0) +
      (details.remainingGst?.inr || 0) -
      (record?.noCostEmiDiscount || 0)
    );
  }
  return (record?.paidAmount || 0) - (record?.noCostEmiDiscount || 0);
};

const computePartialRefundBreakup = (grossAmount) => {
  if (!grossAmount || grossAmount <= 0) return null;
  const refundAmount = grossAmount / 1.18;
  const gst = grossAmount - refundAmount;
  return { refundAmount, gst };
};

const RefernEarnOrders = () => {
  const [managePlanModalVisible, setManagePlanModalVisible] = useState(false);
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({
    totalAmount: 0,
    totalGst: 0,
    totalOrders: 1,
    totalRefundAmount: 0,
    totalRefunds: 0,
  });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    statuses: [], // completed, refunded
    tiers: [], // tier2, tier3, tier4
    platformSetups: [], // freedom, enterprise
    startDate: "",
    endDate: "",
    type: "", // manual, auto
    page: 1,
    pageSize: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());
  const [visible, setVisible] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isOrderOlderThan6Months, setIsOrderOlderThan6Months] = useState(false);
  const [refundData, setRefundData] = useState({
    cancelSubscription: false,
    suspendCreator: false,
    platformSetup: "",
    tier: "",
    managePlatformAccess: false,
    newExpiry: null,
  });
  const [isPartialRefund, setIsPartialRefund] = useState(false);
  const [partialRefundAmount, setPartialRefundAmount] = useState(null);
  const [isLinkedOrders, setIsLinkedOrders] = useState(false);
  const [creatorDetails, setCreatorDetails] = useState(null);
  const [creatorDetailsLoading, setCreatorDetailsLoading] = useState(false);
  const [form] = Form.useForm();

  const getCurrentExpiryDisplay = () => {
    if (creatorDetailsLoading) return "Loading...";
    const expiry = creatorDetails?.accountSettingTierSubscriptionExpiry;
    if (expiry) return moment(expiry).format("YYYY-MM-DD");
    if (creatorDetails?.userTier === "tier1") return "Lifetime";
    return "-";
  };

  const handleFetchCreatorDetails = async (creatorId) => {
    if (!creatorId) return;
    setCreatorDetailsLoading(true);
    try {
      const res = await getAdminCreatorById(creatorId);
      if (res?.code === 200 && res?.result) {
        setCreatorDetails(res.result);
      } else {
        setCreatorDetails(null);
        message.error("Failed to load creator details");
      }
    } catch (err) {
      console.log(err);
      setCreatorDetails(null);
      message.error("Failed to load creator details");
    } finally {
      setCreatorDetailsLoading(false);
    }
  };

  const handleGetRefernEarnOrders = async (csv = false) => {
    setLoading(true);
    let hide;
    if (csv) {
      hide = message.loading("Generating CSV...", 0);
    }
    try {
      const res = await getRefernEarnOrders(filters, csv);
      if (csv) {
        fileDownload(res, "refern_earn_orders.csv");
      } else if (res?.code === 200) {
        setData(res.result.orders.map((item, index) => ({ ...item, index })));
        setStats({
          totalAmount: res.result.totalAmount,
          totalGst: res.result.totalGst,
          totalOrders: res.result.totalOrders,
          totalRefundAmount: res.result.totalRefundAmount,
          totalRefunds: res.result.totalRefunds,
        });
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const handleIssueRefund = async (id, values) => {
    setLoading(true);

    try {
      const res = await refundTierOrder(id, values);
      if (res?.code === 201) {
        handleGetRefernEarnOrders();
        setVisible(false);
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "_id",
      key: "_id",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => moment(value).format("DD MMM YYYY"),
    },
    {
      title: "Creator",
      dataIndex: "userId",
      key: "userId",
      render: (_, record) => (
        <Space size="small" direction="vertical" align="start">
          <Avatar src={record?.profilePicUrl} size={32} />
          <Space size={6} align="center">
            <Tooltip
              title={
                record?.name?.length > CREATOR_NAME_LIMIT ? record?.name : ""
              }
            >
              <Typography>
                {truncate(record?.name, CREATOR_NAME_LIMIT)}
              </Typography>
            </Tooltip>
            <Tooltip
              overlayStyle={{ maxWidth: "none" }}
              overlayInnerStyle={{ maxWidth: "none", width: "max-content" }}
              title={
                <div style={{ lineHeight: 1.6, whiteSpace: "nowrap" }}>
                  <div>Name: {record?.name || "N/A"}</div>
                  <div>Email: {record?.email || "N/A"}</div>
                  <div>Phone: {record?.phone || "N/A"}</div>
                  <div>Host: {record?.host || "N/A"}</div>
                </div>
              }
            >
              <InfoCircleOutlined style={{ color: "#9ca3af" }} />
            </Tooltip>
          </Space>
        </Space>
      ),
    },
    {
      title: "Product",
      dataIndex: "platformSetup",
      key: "platformSetup",
      render: (value, record) => {
        // no platformSetup or saleItem to fall back on — the credits are the
        // product
        if (isAiCreditPurchase(record)) {
          return <span>AI Credits</span>;
        }

        const features = record?.saleItem?.features;
        const isFeaturePurchase =
          record?.saleItem?.subcategory === "feature" ||
          (Array.isArray(features) && features.length > 0);
        if (isFeaturePurchase && features?.length) {
          return <span>{features.join(", ")}</span>;
        }
        return (
          <span style={{ textTransform: "capitalize" }}>
            {record?.newPlan?.platformSetup || value || "-"}
          </span>
        );
      },
    },
    {
      title: "One-time Charge",
      dataIndex: "onetimeCharge",
      key: "onetimeCharge",
      render: (value) =>
        value ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16 }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: "Tier",
      dataIndex: "tier",
      key: "tier",
      render: (value, record) =>
        tierMap[record?.newPlan?.userTier || value] || <span>-</span>,
    },
    {
      title: "Subscription Charge",
      dataIndex: "subscriptionCharge",
      key: "subscriptionCharge",
      render: (value, record) =>
        value && !isAiCreditPurchase(record) ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16 }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: (
        <Space size={4}>
          <span>Paid&nbsp;Amount</span>
          <Tooltip title="Include GST & after TDS (if applicable)">
            <InfoCircleOutlined style={{ color: "#9ca3af", marginLeft: 10 }} />
          </Tooltip>
        </Space>
      ),
      width: 160,
      key: "paidAmount",
      render: (text, record) => {
        return text?.paidAmount ? (
          <Statistic
            value={text?.paidAmount - (record?.noCostEmiDiscount || 0)}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16 }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>-</span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => {
        const statusConfig = {
          completed: { label: "Completed", color: "green" },
          refunded: { label: "Full Refund", color: "red" },
          partial_refunded: { label: "Partial Refund", color: "orange" },
          "refund-initiated": {
            label: "Refund Initiated",
            color: "geekblue",
          },
        };
        const config = statusConfig[value];
        if (config) {
          return <Tag color={config.color}>{config.label}</Tag>;
        }
        return (
          <Tag color="yellow" style={{ textTransform: "capitalize" }}>
            {value}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => {
        const isCompleted = record?.status === "completed";
        const hasRemainingRefundable =
          record?.status === "partial_refunded" &&
          getRemainingRefundable(record) > 0;
        const isPartialIncomplete =
          record?.paymentStatus === "partial_incomplete";
        const canRefund =
          !isPartialIncomplete &&
          ((isCompleted && !record?.refundedTierOrder) ||
            hasRemainingRefundable);

        return (
          <Space>
            {record?.invoice || canRefund ? (
              <Dropdown
                trigger={["click"]}
                overlay={
                  <Menu>
                    {canRefund ? (
                      <Menu.Item
                        key="refund"
                        onClick={() => {
                          if (
                            moment(record?.createdAt).isBefore(
                              moment().subtract(6, "months")
                            )
                          ) {
                            setIsOrderOlderThan6Months(true);
                          } else {
                            setFormValues(record);
                            setIsPartialRefund(false);
                            setPartialRefundAmount(null);
                            setIsLinkedOrders(false);
                            setRefundData({
                              cancelSubscription: false,
                              suspendCreator: false,
                              platformSetup: "",
                              tier: "",
                              managePlatformAccess: false,
                              newExpiry: record?.expiredAt
                                ? moment(record.expiredAt)
                                : null,
                            });
                            if (
                              record?.transactionType === "manual_plan_change"
                            ) {
                              setManagePlanModalVisible(true);
                            } else {
                              setVisible(true);
                            }
                          }
                        }}
                      >
                        {record?.transactionType === "manual_plan_change"
                          ? "Create refund entry"
                          : "Issue refund"}
                      </Menu.Item>
                    ) : null}
                    {record?.invoice ? (
                      <Menu.Item key="invoice">
                        <a
                          href={record?.invoice}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Invoice
                        </a>
                      </Menu.Item>
                    ) : null}
                  </Menu>
                }
              >
                <Button type="primary" shape="circle" icon={<MoreOutlined />} />
              </Dropdown>
            ) : null}
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    handleGetRefernEarnOrders();
  }, [filters]);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  const eligibleForPartial = isPartialRefundEligible(formValues);
  const remainingRefundable = getRemainingRefundable(formValues);
  // Business rule: leave at least ₹100 unrefunded on any partial refund.
  const maxPartialRefundable = Math.max(remainingRefundable - 100, 0);
  let partialRefundError = "";
  if (isPartialRefund && eligibleForPartial) {
    if (maxPartialRefundable <= 0) {
      partialRefundError =
        "Remaining balance is too low for a partial refund. To refund the remaining amount, do a full refund — partial refund isn't possible.";
    } else if (partialRefundAmount == null) {
      partialRefundError = "Please enter a refund amount";
    } else if (partialRefundAmount < 1) {
      partialRefundError = "Refund amount must be at least ₹1";
    } else if (partialRefundAmount > Number(maxPartialRefundable.toFixed(2))) {
      partialRefundError = `Refund amount cannot exceed ₹${maxPartialRefundable.toFixed(
        2
      )}`;
    }
  }
  const partialRefundInvalid =
    isPartialRefund && eligibleForPartial && !!partialRefundError;
  // A tier above tier1 must come with a new expiry date.
  const newExpiryMissing =
    refundData.managePlatformAccess &&
    !!refundData.tier &&
    refundData.tier !== "tier1" &&
    !refundData.newExpiry;
  const partialRefundBreakup = computePartialRefundBreakup(partialRefundAmount);
  const linkedOrdersEligible =
    formValues?.totalRefundableAmount != null &&
    formValues.totalRefundableAmount > remainingRefundable &&
    formValues?.status !== "refunded" &&
    formValues?.status !== "partial_refunded";

  return (
    <>
      <Row gutter={6}>
        <Col span={12}>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            style={{ width: "100%" }}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
        <Col span={12}>
          <Space>
            <DatePicker.RangePicker
              value={[filters.startDate, filters.endDate]}
              onChange={(dates) => {
                setFilters({
                  ...filters,
                  startDate: dates?.[0].startOf("day"),
                  endDate: dates?.[1].endOf("day"),
                });
              }}
              allowClear
            />

            <Button
              type="primary"
              loading={loading}
              onClick={() => {
                handleGetRefernEarnOrders(true);
              }}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Row style={{ marginTop: "20px" }}>
        <Col span={12}>
          <Space>
            <Button
              type={
                filters.platformSetups.length === 0 &&
                filters.tiers.length === 0 &&
                filters.statuses.length === 0 &&
                filters.type === ""
                  ? "primary"
                  : "default"
              }
              onClick={() =>
                setFilters({
                  ...filters,
                  platformSetups: [],
                  tiers: [],
                  statuses: [],
                  type: "",
                  startDate: "",
                  endDate: "",
                  page: 1,
                })
              }
            >
              All
            </Button>

            <Select
              style={{ minWidth: 200 }}
              placeholder="Entry type"
              value={filters.type.length > 0 ? filters.type : undefined}
              onChange={(value) => {
                setFilters({ ...filters, type: value });
              }}
              options={[
                {
                  value: "manual",
                  label: "Manual",
                },
                {
                  value: "auto",
                  label: "Automatic",
                },
              ]}
            />

            <Select
              style={{ minWidth: 200 }}
              placeholder="Product Sales"
              value={filters.platformSetups}
              onChange={(value) => {
                setFilters({ ...filters, platformSetups: value });
              }}
              mode="multiple"
              allowClear
              options={[
                {
                  value: "freedom",
                  label: "Freedom",
                },
                {
                  value: "enterprise",
                  label: "Enterprise",
                },
              ]}
            />

            <Select
              style={{ minWidth: 200 }}
              placeholder="Subscriptions"
              value={filters.tiers}
              onChange={(value) => {
                setFilters({ ...filters, tiers: value });
              }}
              mode="multiple"
              allowClear
              options={[
                {
                  value: "tier2",
                  label: "Pro",
                },
                {
                  value: "tier3",
                  label: "Advanced",
                },
                {
                  value: "tier4",
                  label: "Ultimate",
                },
              ]}
            />

            <Select
              style={{ minWidth: 200 }}
              placeholder="Status"
              value={filters.statuses}
              onChange={(value) => {
                setFilters({ ...filters, statuses: value });
              }}
              mode="multiple"
              options={[
                {
                  value: "completed",
                  label: "Completed",
                },
                {
                  value: "refund-initiated",
                  label: "Refund Initiated",
                },
                {
                  value: "refunded",
                  label: "Full Refund",
                },
                {
                  value: "partial_refunded",
                  label: "Partial Refund",
                },
              ]}
            />
          </Space>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginTop: "16px",
          width: "100%",
        }}
      >
        <Col span={6}>
          <Card title="Total Amount (incl. GST)" bordered={false}>
            <Statistic
              value={stats.totalAmount}
              precision={0}
              formatter={formatIndianNumber}
              valueStyle={{ fontSize: 16 }}
              prefix={<span className="siteInr">&#8377;</span>}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total GST" bordered={false}>
            <Statistic
              value={stats.totalGst}
              precision={0}
              formatter={formatIndianNumber}
              valueStyle={{ fontSize: 16 }}
              prefix={<span className="siteInr">&#8377;</span>}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Orders" bordered={false}>
            <Typography>{stats.totalOrders}</Typography>
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Refunds" bordered={false}>
            <Typography>{stats.totalRefunds}</Typography>
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Refund Value" bordered={false}>
            <Statistic
              value={stats.totalRefundAmount}
              precision={0}
              formatter={formatIndianNumber}
              valueStyle={{ fontSize: 16 }}
              prefix={<span className="siteInr">&#8377;</span>}
            />
          </Card>
        </Col>
      </Row>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total: stats.totalOrders,
          pageSize: filters.pageSize,
          onChange: (p) => setFilters({ ...filters, page: p }),
          showSizeChanger: false,
        }}
        scroll={{ x: "max-content" }}
        rowKey={(record) => record._id}
      />

      <ManagePlanModal
        visible={managePlanModalVisible}
        onClose={() => setManagePlanModalVisible(false)}
        onSave={() => {
          handleGetRefernEarnOrders();
          setManagePlanModalVisible(false);
        }}
        mode="refundingTierOrder"
        userId={formValues?.userId}
        tierOrderId={formValues?._id}
      />

      <Modal
        title="Issue Refund"
        visible={visible}
        onCancel={() => {
          setVisible(false);
          form.resetFields();
          setRefundData({
            cancelSubscription: false,
            suspendCreator: false,
            platformSetup: "",
            tier: "",
            managePlatformAccess: false,
            newExpiry: null,
          });
          setIsPartialRefund(false);
          setPartialRefundAmount(null);
          setIsLinkedOrders(false);
          setCreatorDetails(null);
        }}
        destroyOnClose
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={loading}
            disabled={partialRefundInvalid || newExpiryMissing}
            onClick={() => {
              const values = {};

              // Manage Platform Access — only include platformSetup / tier /
              // tierExpireAt when the admin toggled it on.
              if (refundData.managePlatformAccess) {
                if (refundData.platformSetup) {
                  values.platformSetup = refundData.platformSetup;
                }
                if (refundData.tier) {
                  values.tier = refundData.tier;
                }
                // Send the date only when its picker is shown (paid tier).
                // Basic is lifetime, and the date is pre-filled from the order.
                if (
                  refundData.newExpiry &&
                  refundData.tier &&
                  refundData.tier !== "tier1"
                ) {
                  values.tierExpireAt = moment(
                    refundData.newExpiry
                  ).toISOString();
                }
              }

              // Cancel Subscription applies only to monthly subscription
              // (tier-subscription) refunds.
              if (formValues?.transactionType === "account_tier_subscription") {
                values.cancelSubscription = refundData.cancelSubscription;
              }

              // Suspend Creator applies to the same set that previously
              // showed the Platform Access card.
              if (
                [
                  "account_tier_subscription",
                  "mango_product_purchase",
                  "sale_purchase",
                  "account_page_mango_purchase",
                ].includes(formValues?.transactionType)
              ) {
                values.suspendCreator = refundData.suspendCreator;
              }

              if (linkedOrdersEligible) {
                values.refundAllLinkedOrder = isLinkedOrders;
              }

              if (isPartialRefund && eligibleForPartial) {
                if (partialRefundInvalid) {
                  message.error(partialRefundError);
                  return;
                }
                const breakup =
                  computePartialRefundBreakup(partialRefundAmount);
                if (!breakup) {
                  message.error("Enter a valid partial refund amount");
                  return;
                }
                values.amount = Number(breakup.refundAmount.toFixed(5));
              }

              if (formValues._id) {
                handleIssueRefund(formValues._id, values);
              }
            }}
            style={{ width: "100%" }}
          >
            Save
          </Button>,
        ]}
      >
        {formValues?.user ? (
          <Card style={{ marginBottom: "16px" }}>
            <Space size="small" direction="vertical">
              <Typography style={{}}>{formValues?.user?.name}</Typography>
              <Typography>{formValues?.user?.email}</Typography>
              <Typography>{formValues?.user?.phone}</Typography>
              <Typography>({formValues?.user?.host})</Typography>
            </Space>
          </Card>
        ) : null}

        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, values) =>
            setFormValues((prevState) => ({ ...prevState, ...values }))
          }
        >
          <Form.Item
            label="Refund Amount"
            validateStatus={
              isPartialRefund && partialRefundError ? "error" : ""
            }
            help={isPartialRefund ? partialRefundError : ""}
          >
            {isPartialRefund ? (
              <InputNumber
                value={partialRefundAmount}
                onChange={(value) => setPartialRefundAmount(value)}
                style={{ width: "100%" }}
                prefix={<span className="siteInr">&#8377;</span>}
                placeholder={`Up to ₹${maxPartialRefundable.toFixed(2)}`}
              />
            ) : (
              <Input
                value={(isLinkedOrders
                  ? formValues.totalRefundableAmount
                  : remainingRefundable
                ).toFixed(2)}
                disabled
                prefix={<span className="siteInr">&#8377;</span>}
              />
            )}
            {isPartialRefund && !partialRefundError && partialRefundBreakup ? (
              <div style={{ marginTop: 8 }}>
                <div>
                  Refund amount: <span className="siteInr">&#8377;</span>
                  {partialRefundBreakup.refundAmount.toFixed(2)}
                </div>
                <div>
                  GST: <span className="siteInr">&#8377;</span>
                  {partialRefundBreakup.gst.toFixed(2)}
                </div>
              </div>
            ) : null}
            <span style={{ marginTop: "12px", fontSize: 12 }}>
              {formValues?.onetimeCharge ? (
                <span>
                  One-time Charge:{" "}
                  <span className="siteInr">
                    &#8377;{formValues?.onetimeCharge}
                  </span>
                </span>
              ) : null}
              {formValues?.subscriptionCharge &&
              !isAiCreditPurchase(formValues) ? (
                <span>
                  {formValues?.onetimeCharge ? ", " : ""}
                  Subscription Charge:{" "}
                  <span className="siteInr">
                    &#8377;{formValues?.subscriptionCharge}
                  </span>
                </span>
              ) : null}
            </span>
            {isPartialRefundEligible(formValues) ? (
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={isPartialRefund}
                  onChange={({ target: { checked } }) => {
                    setIsPartialRefund(checked);
                    if (!checked) {
                      setPartialRefundAmount(null);
                    } else {
                      setIsLinkedOrders(false);
                    }
                  }}
                >
                  Partial refund
                </Checkbox>
              </div>
            ) : null}
            {linkedOrdersEligible ? (
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={isLinkedOrders}
                  onChange={({ target: { checked } }) => {
                    setIsLinkedOrders(checked);
                    if (checked) {
                      setIsPartialRefund(false);
                      setPartialRefundAmount(null);
                    }
                  }}
                >
                  Linked Orders
                </Checkbox>
              </div>
            ) : null}
          </Form.Item>

          {!isFeatureSalePurchase(formValues) && (
            <Typography.Title level={5}>Refund Options</Typography.Title>
          )}

          {!isFeatureSalePurchase(formValues) &&
          formValues?.transactionType === "account_tier_subscription" ? (
            <Form.Item name="cancelSubscription" valuePropName="checked">
              <Switch
                checked={refundData?.cancelSubscription}
                onChange={(checked) => {
                  setRefundData({
                    ...refundData,
                    cancelSubscription: checked,
                    // Turning CS OFF while Suspend Creator is ON must also
                    // turn Suspend Creator OFF.
                    ...(!checked && refundData?.suspendCreator
                      ? { suspendCreator: false }
                      : {}),
                  });
                }}
              />{" "}
              Cancel Subscription
            </Form.Item>
          ) : null}

          {!isFeatureSalePurchase(formValues) &&
          [
            "account_tier_subscription",
            "mango_product_purchase",
            "sale_purchase",
            "account_page_mango_purchase",
          ].includes(formValues?.transactionType) ? (
            <Row style={{ gap: "16px" }}>
              <Card style={{ marginBottom: "24px", width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <div>
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                      Manage Platform Access
                    </Typography.Title>
                    <Typography style={{ fontSize: 12, color: "#6b7280" }}>
                      Reconfigure current set up, tiers and expiration settings
                    </Typography>
                  </div>
                  <Switch
                    checked={refundData?.managePlatformAccess}
                    onChange={(checked) => {
                      // Enabling MPA is mutually exclusive with Suspend
                      // Creator — force SC off.
                      const mpaOnPatch = refundData?.suspendCreator
                        ? { suspendCreator: false }
                        : {};
                      const mpaOffPatch = {
                        platformSetup: "",
                        tier: "",
                        newExpiry: formValues?.expiredAt
                          ? moment(formValues.expiredAt)
                          : null,
                      };
                      setRefundData({
                        ...refundData,
                        managePlatformAccess: checked,
                        ...(checked ? mpaOnPatch : mpaOffPatch),
                      });
                      if (checked) {
                        handleFetchCreatorDetails(formValues?.userId);
                      } else {
                        setCreatorDetails(null);
                      }
                    }}
                  />
                </Row>

                {refundData?.managePlatformAccess ? (
                  <>
                    <div
                      style={{
                        background: "#fef3c7",
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 16,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <div>
                        <Typography style={{ fontSize: 11, color: "#6b7280" }}>
                          CURRENT SETUP
                        </Typography>
                        <Typography>
                          {creatorDetailsLoading
                            ? "Loading..."
                            : platformSetupDisplayMap[
                                creatorDetails?.whitelabelPlanType
                              ] ||
                              creatorDetails?.whitelabelPlanType ||
                              "TagMango"}
                        </Typography>
                      </div>
                      <div>
                        <Typography style={{ fontSize: 11, color: "#6b7280" }}>
                          CURRENT TIER
                        </Typography>
                        <Typography>
                          {creatorDetailsLoading
                            ? "Loading..."
                            : tierMap[creatorDetails?.userTier] || "-"}
                        </Typography>
                      </div>
                      <div>
                        <Typography style={{ fontSize: 11, color: "#6b7280" }}>
                          EXPIRY DATE
                        </Typography>
                        <Typography>{getCurrentExpiryDisplay()}</Typography>
                      </div>
                    </div>

                    <Row gutter={12} style={{ marginTop: 16 }}>
                      <Col span={12}>
                        <Typography
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          Update Setup To
                        </Typography>
                        <Select
                          allowClear
                          value={refundData?.platformSetup || undefined}
                          style={{ width: "100%" }}
                          dropdownStyle={{ zIndex: 3000 }}
                          onChange={(value) => {
                            setRefundData({
                              ...refundData,
                              platformSetup: value,
                              tier: value === "tagmango" ? "tier1" : "",
                            });
                          }}
                          options={[
                            { value: "tagmango", label: "TagMango" },
                            { value: "freedom", label: "Freedom" },
                            { value: "enterprise", label: "Enterprise" },
                          ]}
                        />
                      </Col>
                      <Col span={12}>
                        <Typography
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          Update Tier To
                        </Typography>
                        <Select
                          allowClear
                          value={refundData?.tier || undefined}
                          style={{ width: "100%" }}
                          dropdownStyle={{ zIndex: 3000 }}
                          disabled={!refundData?.platformSetup}
                          onChange={(value) => {
                            setRefundData({
                              ...refundData,
                              tier: value,
                              ...(value === "tier1" ? { newExpiry: null } : {}),
                            });
                          }}
                          options={
                            tierOptionsBySetup[refundData?.platformSetup] || []
                          }
                        />
                      </Col>
                    </Row>

                    {refundData?.tier && refundData.tier !== "tier1" ? (
                      <div style={{ marginTop: 12 }}>
                        <Typography
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          New Expiry Date{" "}
                          <span style={{ color: "#ff4d4f" }}>*</span>
                        </Typography>
                        <DatePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          status={newExpiryMissing ? "error" : undefined}
                          value={refundData?.newExpiry || null}
                          onChange={(value) => {
                            setRefundData({
                              ...refundData,
                              newExpiry: value,
                            });
                          }}
                        />
                        {newExpiryMissing ? (
                          <Typography
                            style={{
                              fontSize: 12,
                              color: "#ff4d4f",
                              marginTop: 4,
                            }}
                          >
                            Please select a new expiry date
                          </Typography>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </Card>

              <Card style={{ marginBottom: "24px", width: "100%" }}>
                <Row style={{ gap: "16px" }} justify="space-between">
                  <Typography.Title level={5}>Suspend Creator</Typography.Title>
                  <Switch
                    checked={
                      refundData?.suspendCreator ||
                      refundData?.platformSetup === "tagmango"
                    }
                    onChange={() => {
                      const isForcedSuspension =
                        refundData?.platformSetup === "tagmango";

                      if (isForcedSuspension) return;

                      const nextSuspend = !refundData?.suspendCreator;
                      const clearingMPA =
                        nextSuspend && refundData?.managePlatformAccess;

                      setRefundData({
                        ...refundData,
                        suspendCreator: nextSuspend,
                        ...(nextSuspend
                          ? {
                              // Turning SC ON forces CS ON and MPA OFF
                              // (mutually exclusive with MPA).
                              cancelSubscription: true,
                              managePlatformAccess: false,
                              platformSetup: "",
                              tier: "",
                              newExpiry: formValues?.expiredAt
                                ? moment(formValues.expiredAt)
                                : null,
                            }
                          : {}),
                      });

                      if (clearingMPA) {
                        setCreatorDetails(null);
                      }
                    }}
                  />
                </Row>

                <Typography style={{ marginTop: "8px" }}>
                  {refundData?.platformSetup === "tagmango"
                    ? "You are downgrading the creator from Whitelable setup to TagMango, suspension of this creator's platform is mandatory."
                    : "Enabling this would suspend the creator upon refunding"}
                </Typography>
              </Card>
            </Row>
          ) : null}
        </Form>
      </Modal>
      <ConfirmationModal
        open={isOrderOlderThan6Months}
        onConfirm={() => {
          setIsOrderOlderThan6Months(false);
        }}
        onCancel={() => setIsOrderOlderThan6Months(false)}
        title="Refund Not Eligible"
        customContent={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              gap: 12,
              marginTop: 12,
            }}
          >
            <Typography.Text>Not Eligible for Refund,</Typography.Text>
            <Typography.Text>
              This order is older than 6 months and its not eligible for refund.
            </Typography.Text>
          </div>
        }
        confirmButtonText="Okay"
        showCancelButton={false}
      />
    </>
  );
};

export default RefernEarnOrders;
